import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "framer-motion";
import { useState, useRef, useEffect } from "react";
import { Menu, ChevronDown } from "lucide-react";
import logo from "@/assets/responsive/logos/Final Logo4-1-400.webp";
import logo2x from "@/assets/responsive/logos/Final Logo4-1-800.webp";
import logoDark from "@/assets/responsive/logos/Final Logo4-2-400.webp";
import logoDark2x from "@/assets/responsive/logos/Final Logo4-2-800.webp";
import logoLight from "@/assets/small-size.png";
import { menuData } from "@/data/menu";
import type { MenuItem } from "@/data/menu";
import { MegaMenu } from "./MegaMenu";
import { BurgerMenu } from "./BurgerMenu";
import { ThemeToggle } from "./ThemeToggle";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

function getMegaMenuWidth(item: MenuItem) {
  const columnCount = Math.min(item.columns.length, 5);
  return columnCount <= 1 ? 280 : columnCount === 2 ? 560 : columnCount === 3 ? 840 : 1080;
}

export default function Navbar() {
  const [hoverKey, setHoverKey] = useState<string | null>(null);
  const [burgerOpen, setBurgerOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [menuLeft, setMenuLeft] = useState<number | null>(null);

  const closeTimeout = useRef<NodeJS.Timeout | null>(null);
  const navContainerRef = useRef<HTMLDivElement | null>(null);
  const desktopNavRef = useRef<HTMLElement | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const { scrollY } = useScroll();



  useMotionValueEvent(scrollY, "change", (y) => {
    const prev = scrollY.getPrevious() ?? 0;

    setScrolled(y > 40);

    if (y > 120 && y > prev) {
      setHidden(true);
    } else {
      setHidden(false);
    }
  });

  const getMenuItem = (key: string) => menuData.find((m) => m.key === key);

  const hovered = hoverKey ? getMenuItem(hoverKey) : undefined;

  const getActiveMenuKey = (pathname: string) => {
    const matchedMenu = menuData.find((menuItem) =>
      menuItem.columns?.some((column) =>
        column.items.some((item) => item.path === pathname),
      ),
    );

    return matchedMenu ? matchedMenu.key : null;
  };

  const activeMenuKey = getActiveMenuKey(location.pathname);
  const alwaysVisibleLinks: { key: string; label: string; path: string }[] = [];



  const positionMenu = (key: string, trigger: HTMLElement) => {
    const navContainer = navContainerRef.current;
    const item = getMenuItem(key);

    if (!navContainer || !item) {
      setMenuLeft(null);
      return;
    }

    const containerRect = navContainer.getBoundingClientRect();
    const desktopNavRect = desktopNavRef.current?.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
    const viewportWidth = typeof window === "undefined" ? containerRect.width : window.innerWidth;
    const navStart = desktopNavRect ? desktopNavRect.left - containerRect.left : 0;
    const menuWidth = Math.min(
      getMegaMenuWidth(item),
      viewportWidth - 32,
      containerRect.width - navStart,
    );
    const triggerCenter = triggerRect.left - containerRect.left + triggerRect.width / 2;
    const maxLeft = Math.max(containerRect.width - menuWidth, 0);

    setMenuLeft(Math.min(Math.max(triggerCenter - menuWidth / 2, navStart), maxLeft));
  };

  const openMenu = (key: string, trigger?: HTMLElement) => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
    }

    if (trigger) {
      positionMenu(key, trigger);
    } else {
      setMenuLeft(null);
    }

    setHoverKey(key);
  };

  const closeMenuNow = () => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
    }
    setHoverKey(null);
    setMenuLeft(null);
  };

  const closeMenu = () => {
    closeTimeout.current = setTimeout(() => {
      setHoverKey(null);
      setMenuLeft(null);
    }, 180);
  };

  useEffect(() => {
    closeMenuNow();
  }, [location.pathname]);

  return (
    <>
      <style>{`
        .bot-wrap {
          width: 56px;
          height: 62px;
          position: relative;
          flex: 0 0 auto;
        }

        .bot-svg {
          width: 100%;
          height: 100%;
          overflow: visible;
          transform-box: fill-box;
          transform-origin: center;
        }

        .logo-composite {
          position: relative;
          display: block;
          aspect-ratio: 1999 / 874;
          flex: 0 0 auto;
          transition: height 0.3s ease;
        }

        .logo-composite-image {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .logo-composite-image-dark {
          display: none;
        }

        .dark .logo-composite-image-light {
          display: none;
        }

        .dark .logo-composite-image-dark {
          display: block;
        }

        .logo-bot {
          position: absolute;
          left: 67.65%;
          top: 45.25%;
          width: 19.55%;
          height: auto;
          z-index: 1;
          transform: none;
          transform-origin: center;
        }

        .logo-bot .bot-svg {
          display: block;
          width: 100%;
          height: auto;
        }

        .bot-outer-shape {
          fill: #e00000;
        }

        .bot-antenna-ball {
          animation: bot-antenna-flash 26s steps(1, end) infinite;
        }

        .bot-inner-face {
          fill: #fff;
        }

        .bot-ear {
          fill: #050505;
        }

        .bot-antenna-stem {
          fill: #333333;
        }

        .dark .bot-ear {
          fill: #333333;
        }

        .bot-eye-normal,
        .bot-eye-start-dash,
        .bot-eye-blink,
        .bot-wink-eyes,
        .bot-thinking-eyes,
        .bot-star-eyes,
        .bot-mouth-star,
        .bot-mouth-start-dash,
        .bot-mouth-happy,
        .bot-mouth-thinking {
          transform-box: fill-box;
          transform-origin: center;
        }

        .bot-eye-normal {
          animation: bot-normal-eyes 26s ease-in-out infinite;
        }

        .bot-eye-start-dash {
          opacity: 0;
          animation: bot-start-dash-eyes 26s ease-in-out infinite;
        }

        .bot-eye-blink {
          opacity: 0;
          animation: bot-blink-eyes 26s ease-in-out infinite;
        }

        .bot-wink-eyes {
          fill: none;
          opacity: 0;
          animation: bot-wink-eyes 26s steps(1, end) infinite;
        }

        .bot-wink-eye-right,
        .bot-thinking-eye {
          transform-box: fill-box;
          transform-origin: center;
        }

        .bot-eye-pupil {
          fill: #050505;
          shape-rendering: geometricPrecision;
        }

        .bot-eye-line {
          fill: none;
          stroke: #050505;
          stroke-width: 8;
          stroke-linecap: round;
        }

        .bot-eye-dash {
          fill: none;
          stroke: #050505;
          stroke-width: 4;
          stroke-linecap: round;
        }

        .bot-thinking-look-left,
        .bot-thinking-look-right {
          transform-box: fill-box;
          transform-origin: center;
          animation: bot-thinking-look 26s ease-in-out infinite;
        }

        .bot-eye-ring {
          fill: none;
          stroke: #050505;
          stroke-width: 3;
        }

        .bot-mouth-happy {
          fill: none;
          stroke: #050505;
          stroke-width: 3.1;
          stroke-linecap: round;
          animation: bot-happy-mouth 26s ease-in-out infinite;
        }

        .bot-mouth-start-dash {
          fill: none;
          stroke: #050505;
          stroke-width: 3.1;
          stroke-linecap: round;
          opacity: 0;
          animation: bot-start-dash-mouth 26s ease-in-out infinite;
        }

        .bot-thinking-eyes {
          opacity: 0;
          animation: bot-thinking-eyes 26s ease-in-out infinite;
        }

        .bot-mouth-thinking {
          fill: none;
          stroke: #050505;
          stroke-width: 3.4;
          stroke-linecap: round;
          stroke-linejoin: round;
          opacity: 0;
          animation: bot-thinking-mouth 26s ease-in-out infinite;
        }

        .bot-star-eyes {
          fill: #050505;
          stroke: #050505;
          stroke-width: 2;
          stroke-linejoin: round;
          opacity: 0;
          animation: bot-star-eyes 26s ease-in-out infinite;
        }

        .bot-mouth-star {
          fill: none;
          stroke: #050505;
          stroke-width: 3.1;
          stroke-linecap: round;
          opacity: 0;
          animation: bot-star-mouth 26s ease-in-out infinite;
        }

        @keyframes bot-normal-eyes {
          0%, 8.4% {
            opacity: 0;
            transform: translateX(0) scaleY(0.08);
          }
          9.4% {
            opacity: 1;
            transform: translateX(0) scaleY(0.35);
          }
          11.2%, 16.8%, 20.2%, 27%, 39.6%, 43.2% {
            opacity: 1;
            transform: translateX(0) scaleY(1);
          }
          17.2%, 19.5% {
            opacity: 1;
            transform: translateX(0) scaleY(0.25);
          }
          17.5%, 19.2% {
            opacity: 0;
            transform: translateX(0) scaleY(0.08);
          }
          25%, 28% {
            opacity: 1;
            transform: translateX(-7px) scaleY(1);
          }
          30%, 34% {
            opacity: 1;
            transform: translateX(7px) scaleY(1);
          }
          36.3%, 38.3% {
            opacity: 1;
            transform: translateX(0) scaleY(1);
          }
          36.35%, 38.25% {
            opacity: 0;
            transform: translateX(0) scaleY(1);
          }
          43.299% {
            opacity: 1;
            transform: translateX(0) scaleY(1);
          }
          43.3%, 63.8%, 71.2% {
            opacity: 0;
            transform: translateX(0) scaleY(1);
          }
          63.9%, 68.45%, 70.8%, 82.2%, 91.6% {
            opacity: 1;
            transform: translateX(0) scaleY(1);
          }
          92.3% {
            opacity: 1;
            transform: translateX(0) scaleY(0.68);
          }
          93% {
            opacity: 1;
            transform: translateX(0) scaleY(0.38);
          }
          93.55% {
            opacity: 1;
            transform: translateX(0) scaleY(0.18);
          }
          71.2%, 81.8%, 93.65%, 100% {
            opacity: 0;
            transform: translateX(0) scaleY(0.18);
          }
        }

        @keyframes bot-start-dash-eyes {
          0%, 9.399%, 93.7%, 100% {
            opacity: 1;
            transform: scaleX(1);
          }
          9.4%, 93.699% {
            opacity: 0;
            transform: scaleX(1);
          }
        }

        @keyframes bot-blink-eyes {
          0%, 17.1%, 19.6%, 100% {
            opacity: 0;
            transform: scaleY(0.08);
          }
          17.25%, 19.45% {
            opacity: 1;
            transform: scaleY(0.35);
          }
          17.5%, 19.2% {
            opacity: 1;
            transform: scaleY(1);
          }
        }

        @keyframes bot-wink-eyes {
          0%, 36.349%, 38.251%, 100% {
            opacity: 0;
          }
          36.35%, 38.25% {
            opacity: 1;
          }
        }

        @keyframes bot-happy-mouth {
          0%, 8.6% {
            opacity: 0;
            transform: translateX(0) scaleY(0.2);
          }
          9.6% {
            opacity: 0.35;
            transform: translateX(0) scaleY(0.55);
          }
          11.4%, 27%, 35.6%, 40.4%, 43.2%, 63.9%, 68.45%, 70.8%, 82.2%, 91.6% {
            opacity: 1;
            transform: translateX(0) scaleY(1);
          }
          25%, 28% {
            opacity: 1;
            transform: translateX(-3px) translateY(0.5px);
          }
          30%, 34% {
            opacity: 1;
            transform: translateX(3px) translateY(0.5px);
          }
          92.3% {
            opacity: 1;
            transform: translateX(0) scaleY(0.68);
          }
          93% {
            opacity: 1;
            transform: translateX(0) scaleY(0.38);
          }
          93.55% {
            opacity: 1;
            transform: translateX(0) scaleY(0.18);
          }
          43.29%, 62.8%, 63.8%, 71.2%, 81.8%, 93.65%, 100% {
            opacity: 0;
            transform: translateX(0) scaleY(0.18);
          }
        }

        @keyframes bot-start-dash-mouth {
          0%, 9.399%, 93.7%, 100% {
            opacity: 1;
            transform: scaleX(1);
          }
          9.4%, 93.699% {
            opacity: 0;
            transform: scaleX(1);
          }
        }

        @keyframes bot-antenna-flash {
          0%, 43%, 44.5%, 47%, 49.5%, 52%, 54.5%, 57%, 59.5%, 62%, 63.8%, 100% {
            fill: #e00000;
          }
          44.51%, 46.99%, 49.51%, 51.99%, 54.51%, 56.99%, 59.51%, 61.99% {
            fill: #333333;
          }
        }

        @keyframes bot-thinking-eyes {
          0%, 43.29%, 63.81%, 100% {
            opacity: 0;
            transform: translate(0, 0) scale(0.94);
          }
          43.3% {
            opacity: 1;
            transform: translate(0, -1.3px) scale(1);
          }
          44.3%, 63.8% {
            opacity: 1;
            transform: translate(0, -1.3px) scale(1);
          }
          51.5%, 57.5%, 61.5% {
            opacity: 1;
            transform: translate(0, -1.8px) scale(1.02);
          }
        }

        @keyframes bot-thinking-look {
          0%, 43.29%, 63.81%, 100% {
            transform: translate(0, 0);
          }
          43.3%, 47.2% {
            transform: translate(0, 0);
          }
          49.2%, 53.5% {
            transform: translate(-3px, 0.4px);
          }
          55.5%, 59.8% {
            transform: translate(3px, 0.4px);
          }
          61.8%, 63.8% {
            transform: translate(0, 0);
          }
        }

        @keyframes bot-thinking-mouth {
          0%, 43.29%, 63.81%, 100% {
            opacity: 0;
            transform: translate(0, 0) rotate(0deg) scale(0.9, 0.9);
          }
          43.3%, 47.2% {
            opacity: 1;
            transform: translate(0, -0.5px) rotate(0deg) scale(1, 1);
          }
          49.2%, 53.5% {
            opacity: 1;
            transform: translate(-2.2px, -0.2px) rotate(-7deg) scale(0.94, 1.08);
          }
          55.5%, 59.8% {
            opacity: 1;
            transform: translate(2.2px, -0.2px) rotate(7deg) scale(0.94, 1.08);
          }
          61.8%, 63.8% {
            opacity: 1;
            transform: translate(0, -0.5px) rotate(0deg) scale(1, 1);
          }
        }

        @keyframes bot-star-eyes {
          0%, 70.8%, 82.2%, 100% {
            opacity: 0;
            transform: translateY(2px) scale(0.72) rotate(-12deg);
          }
          71.2% {
            opacity: 0.55;
            transform: translateY(0.8px) scale(0.9) rotate(-4deg);
          }
          71.6%, 75.4%, 81.4% {
            opacity: 1;
            transform: translateY(0) scale(1) rotate(0deg);
          }
          81.8% {
            opacity: 0.45;
            transform: translateY(0.8px) scale(0.86) rotate(-4deg);
          }
          76.2%, 77.8% {
            opacity: 1;
            transform: translateY(-0.5px) scale(1.08) rotate(8deg);
          }
        }

        @keyframes bot-star-mouth {
          0%, 70.8%, 82.2%, 100% {
            opacity: 0;
            transform: translateY(1px) scaleY(0.6);
          }
          71.2% {
            opacity: 0.55;
            transform: translateY(0.5px) scaleY(0.85);
          }
          71.6%, 75.4%, 81.4% {
            opacity: 1;
            transform: translateY(0) scaleY(1);
          }
          81.8% {
            opacity: 0.45;
            transform: translateY(0.5px) scaleY(0.8);
          }
        }
      `}</style>
      <motion.header
        initial={{ y: 0 }}
        animate={{ y: hidden ? -120 : 0 }}
        transition={{
          duration: 0.45,
          ease: [0.22, 1, 0.36, 1],
        }}
        className={`top-0 z-50 fixed inset-x-0 flex justify-center px-4 pt-4 ${scrolled
            ? "bg-gradient-to-b from-background/100 via-background/40 to-transparent"
            : "bg-transparent backdrop-blur-0"
          }`}
        onMouseLeave={closeMenu}
        onMouseEnter={() => {
          if (closeTimeout.current) {
            clearTimeout(closeTimeout.current);
          }
        }}
      >
        <motion.div
          ref={navContainerRef}
          animate={{
            borderRadius: scrolled ? 999 : 16,
          }}
          transition={{
            duration: 0.4,
            ease: [0.22, 1, 0.36, 1],
          }}
          className={`relative flex w-full max-w-[1440px] items-center justify-between mt-2 md:mt-3 md:gap-4 px-1 py-1 md:px-4 md:py-2.5 md:px-6${scrolled
            ? "glass border border-border/60 shadow-[0_10px_40px_-15px_rgba(0,0,0,0.25)] backdrop-blur-2xl"
            : "border border-transparent bg-transparent"
            }`}
        >
          {/* Left */}
          <div className="flex items-center gap-3">
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    aria-label="Open menu"
                    onClick={() => setBurgerOpen(true)}
                    className="place-items-center grid bg-background/40 hover:bg-background/80 border border-border/60 rounded-full w-[45px] h-[45px] transition"
                  >
                    <Menu className="w-[18px] h-[18px]" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Menu</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Link
              to="/"
              className="
                absolute left-1/2 -translate-x-1/2
                flex items-center gap-2
                md:static md:translate-x-0
              "
            >
              {/* drop-shadow-[0_0_12px_rgba(255,255,255,0.80)] dark:drop-shadow-[0_0_12px_rgba(255,255,255,0.80)] */}
              {/* <img
                src={logoLight}
                alt="Santhila"
                className="dark:hidden block w-auto h-9 md:h-[48px]"
              /> */}
              {/* Light mode logo */}
              <span className={cn('logo-composite', scrolled ? 'h-[40px] md:h-[50px]' : ' md:m-0 h-[85px] md:h-[88px]')}>
                <img
                  src={logo}
                  srcSet={`${logo} 400w, ${logo2x} 800w`}
                  sizes="(min-width: 768px) 180px, 120px"
                  width={205}
                  height={90}
                  alt='Santhila logo - Enterprise AI solutions, Industrial IoT platforms, and product engineering'
                  className='logo-composite-image logo-composite-image-light'
                />
                <img
                  src={logoDark}
                  srcSet={`${logoDark} 400w, ${logoDark2x} 800w`}
                  sizes="(min-width: 768px) 180px, 120px"
                  width={205}
                  height={90}
                  alt='Santhila logo - Enterprise AI solutions, Industrial IoT platforms, and product engineering'
                  className='logo-composite-image logo-composite-image-dark'
                />

                <div className='bot-wrap logo-bot' aria-hidden='true'>
                  <svg
                    className='bot-svg'
                    viewBox='0 0 120 104'
                    xmlns='http://www.w3.org/2000/svg'
                    role='img'
                    aria-label='Animated bot logo'
                  >
                    <defs>
                      <path id='bot-star-eye-shape' d='M0 -9.5L3 -3L10 -2.2L4.8 2.6L6.2 9.5L0 6L-6.2 9.5L-4.8 2.6L-10 -2.2L-3 -3Z' />
                    </defs>
                    <path className='bot-ear' d='M0 58C0 51 6 46 16 46H25V80H16C6 80 0 75 0 68V58Z' />
                    <path className='bot-ear' d='M95 46H104C114 46 120 51 120 58V68C120 75 114 80 104 80H95V46Z' />
                    <path className='bot-antenna-stem' d='M57.5 7H62.5V23H57.5Z' />
                    <circle className='bot-outer-shape bot-antenna-ball' cx='60' cy='7' r='7' />
                    <path className='bot-outer-shape' d='M8 55C8 34 21 23 40 23H80C99 23 112 34 112 55V68C112 91 99 103 80 103H40C21 103 8 91 8 68V55Z' />
                    <path className='bot-inner-face' d='M18 56C18 41 29 32 43 32H77C91 32 102 41 102 56V69C102 86 91 95 77 95H43C29 95 18 86 18 69V56Z' />

                    <g className='bot-eye-normal'>
                      <path className='bot-eye-pupil bot-eye-line' d='M42 49V63' />
                      <path className='bot-eye-pupil bot-eye-line' d='M78 49V63' />
                    </g>

                    <g className='bot-eye-start-dash'>
                      <path className='bot-eye-pupil bot-eye-dash' d='M36.5 58.5H49.5' />
                      <path className='bot-eye-pupil bot-eye-dash' d='M70.5 58.5H83.5' />
                    </g>

                    <g className='bot-eye-blink'>
                      <path className='bot-eye-pupil bot-eye-dash' d='M37 57H50' />
                      <path className='bot-eye-pupil bot-eye-dash' d='M70 57H83' />
                    </g>

                    <g className='bot-wink-eyes'>
                      <path className='bot-eye-pupil bot-eye-dash' d='M36.5 57H49.5' />
                      <path className='bot-wink-eye-right bot-eye-pupil bot-eye-line' d='M78 49V63' />
                    </g>

                    <g className='bot-thinking-eyes'>
                      <g className='bot-thinking-eye'>
                        <g className='bot-thinking-look-left'>
                          <path className='bot-eye-pupil bot-eye-line' d='M42 42V56' />
                        </g>
                      </g>
                      <g className='bot-thinking-eye'>
                        <g className='bot-thinking-look-right'>
                          <path className='bot-eye-pupil bot-eye-line' d='M78 42V56' />
                        </g>
                      </g>
                    </g>

                    <g className='bot-star-eyes'>
                      <use href='#bot-star-eye-shape' x='42.5' y='53' />
                      <use href='#bot-star-eye-shape' x='77.5' y='53' />
                    </g>

                    <path className='bot-mouth-start-dash' d='M53 75.5H67' />
                    <path className='bot-mouth-happy' d='M50 78C55 82 65 82 70 78' />
                    <path className='bot-mouth-thinking' d='M54 78C57 75.5 63 75.5 66 78' />
                    <path className='bot-mouth-star' d='M48 76C53 83 67 83 72 76' />
                  </svg>
                </div>
              </span>
            </Link>
          </div>

          {/* Center */}
          <nav ref={desktopNavRef} className="hidden lg:flex flex-1 justify-center items-center min-w-0">
            <ul className="flex items-center gap-3 laptop:gap-4 whitespace-nowrap">
              {menuData.map((m) =>
                m.key === "resources" || m.key === "contact-us" || m.hideInDesktop ? null : (
                  <li
                    key={m.key}
                    onMouseEnter={(event) => {
                      openMenu(m.key, event.currentTarget);
                    }}
                    className="py-2"
                  >
                    <button
                      onClick={() => {
                        if (m.key === "case-studies") {
                          navigate("/case-studies");
                          closeMenuNow();
                        }
                      }}
                      className={
                        `flex items-center gap-1 font-medium text-[13px] laptop:text-[14px] cursor-pointer ${activeMenuKey === m.key ? "text-brand" : "text-foreground"
                        }`
                      }
                      data-active={activeMenuKey === m.key}
                    >
                      {m.label}

                      <ChevronDown className="opacity-70 w-3.5 h-3.5" />
                    </button>
                  </li>
                ),
              )}
              {alwaysVisibleLinks.map((link) => (
                <li key={link.key} className="py-2">
                  <button
                    onClick={() => navigate(link.path)}
                    className={`font-medium text-[13px] laptop:text-[14px] cursor-pointer ${location.pathname === link.path
                      ? "text-brand"
                      : "text-foreground"
                      }`}
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* Right */}
          <div className="flex items-center gap-2">
            <ThemeToggle />

            {/* <a
              href="#contact"
              className="inline-flex items-center bg-brand hover:opacity-90 shadow-[0_8px_24px_-10px_var(--color-brand)] px-5 py-2.5 rounded-full font-semibold text-brand-foreground text-sm transition"
            >
              Talk to us
            </a> */}
          </div>

          {/* Mega Menu */}
          <AnimatePresence>
            {hovered && (
              <MegaMenu
                key={hovered.key}
                item={hovered}
                left={menuLeft ?? undefined}
                activePath={location.pathname}
                onItemClick={closeMenuNow}
              />
            )}
          </AnimatePresence>
        </motion.div>
      </motion.header>

      {/* Mobile Burger Menu */}
      <BurgerMenu open={burgerOpen} onClose={() => setBurgerOpen(false)} />
    </>
  );
}
